import { useEffect, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
  getStartupScopes,
  useStartups,
  useScopeConversations,
  selectProjectScopes,
  useCreditLimitBanner,
  useHeaderConfig,
  useSidebarConfig,
  type StartupDto,
} from '@ideoz/core';
import {
  AppLayout,
  Header,
  NotFoundState,
  buildStartupNavItems,
  showToast,
  type StartupNavSection,
} from '@ideoz/ui';
import { NewScopeAction } from '../modules/workScopes';

/** Sections available inside the scope shell (Files / Artifacts / Clarify).
 *  Kept in sync with the ids used in `useSidebarConfig({ pageType: 'scope' })`
 *  so setting `isActive` here just matches on id. */
export type ScopeNavSection = 'files' | 'artifacts' | 'clarify';
import { useWebNavigation } from '../hooks/useWebNavigation';
import { useAuthDialog } from '../hooks/useAuthDialog';

/**
 * Common shell for startup-level and scope-level sub-pages.
 *
 * - Startup mode (default): pass `activeSection`. Nav shows Home / Work Scopes /
 *   Artifacts / Files / Design system / Startup Profile / Integrations. Back
 *   defaults to `/:slug/home`.
 * - Scope mode: pass `scopeSection` (URL must include `:scopeId`). Nav shows
 *   Clarify / Files / Artifacts. Back defaults to the scope's home
 *   (`/:slug/:scopeId` — the conversation page). "New Scope" is hidden in
 *   scope mode.
 *
 * Menu items for both modes live in `packages/ui/src/lib/startupNavItems.ts`
 * and `scopeNavItems.ts` — single source of truth.
 */
type ShellProps = {
  /** Override the default back destination. */
  backTo?: (slug: string, scopeId?: string) => string;
  /** Hide the back button entirely (e.g. on the Home landing page). */
  hideBackButton?: boolean;
  /** Override the header title (e.g. a detail breadcrumb like "Integrations / Notion"). */
  headerTitle?: string;
  /** Extra header actions, rendered before the "New Scope" button (e.g. "Suggest scopes"). */
  headerActions?: ReactNode;
  /**
   * Override the built-in "New Scope" header action. Pages that own their own
   * create flow (e.g. Work Scopes, which shares its ICE facade with the list)
   * pass `false` and render their own button in `headerActions`.
   */
  showNewScopeAction?: boolean;
  children: (ctx: { startup: StartupDto; startupId: string }) => ReactNode;
} & (
  | { activeSection: StartupNavSection; scopeSection?: never }
  | { scopeSection: ScopeNavSection; activeSection?: never }
);

type StartupSubPageShellProps = ShellProps;

export function StartupSubPageShell(props: StartupSubPageShellProps): JSX.Element {
  const { backTo, hideBackButton = false, children } = props;
  const isScopeMode = 'scopeSection' in props && !!props.scopeSection;
  const navigate = useNavigate();
  const { startupSlug, scopeId } = useParams<{ startupSlug: string; scopeId: string }>();
  const dispatch = useAppDispatch();
  const {
    data: startups = [],
    isFetching: isFetchingStartups,
    isFetched: hasAttemptedLoad,
    isError: isStartupsError,
  } = useStartups();
  const navigation = useWebNavigation();
  const authDialog = useAuthDialog();

  const matchedStartup = startups.find((s: StartupDto) => s.slug === startupSlug) ?? null;
  const startupId = matchedStartup?.id ?? null;
  const slug = startupSlug ?? '';
  const scopeParam = scopeId ?? '';

  // Scope name for the header. Reads redux first (populated by
  // `getStartupScopes`) so switching between scope sub-pages is instant.
  // If missing, fetches the list once for the current startup.
  const scopes = useAppSelector(selectProjectScopes);
  const matchedScope =
    isScopeMode && scopeParam ? (scopes.find((s: any) => s.id === scopeParam) ?? null) : null;
  const hasScopeInStore = !!matchedScope;
  useEffect(() => {
    if (!isScopeMode || !startupId || hasScopeInStore) return;
    dispatch(getStartupScopes({ startupId }));
  }, [dispatch, isScopeMode, startupId, hasScopeInStore]);

  // Recent-chats list for the scope sidebar. Shared React-Query cache (keyed by
  // scope) so the list is fetched once on entering the scope and stays
  // populated across the scope's sub-pages (Files / Artifacts) instead of
  // resetting to empty. `enabled` only in scope mode.
  const { data: scopeConversationList = [], isLoading: isLoadingScopeConversations } =
    useScopeConversations(startupId, isScopeMode ? scopeParam : undefined);

  const SCOPE_SECTION_LABEL: Record<ScopeNavSection, string> = {
    clarify: 'Clarify',
    files: 'Files',
    artifacts: 'Artifacts',
  };
  // Header title for scope pages: "Scope name / Section".
  // Falls back to the section label alone until the scope name resolves.
  const scopeHeaderTitle =
    isScopeMode && props.scopeSection
      ? matchedScope?.name
        ? `${matchedScope.name} / ${SCOPE_SECTION_LABEL[props.scopeSection]}`
        : SCOPE_SECTION_LABEL[props.scopeSection]
      : (matchedStartup?.name ?? '');

  const headerConfig = useHeaderConfig({
    onNavigateToProfile: navigation.toProfile,
    onNavigateToBilling: navigation.toBilling,
    onNavigateToReport: navigation.toReportProblem,
    onLoginClick: authDialog.openLogin,
    onRegisterClick: authDialog.openRegister,
    showToast,
  });

  const creditLimitBanner = useCreditLimitBanner();

  // Scope pages reuse the same sidebar the Clarify (conversation) page shows —
  // Files, Artifacts, Clarify, Prototyper, New chat, Recent chats. Built by
  // `useSidebarConfig({ pageType: 'scope' })`. We override the `isActive` flag
  // on the item matching `props.scopeSection` so the sub-page highlights
  // correctly (the hook only knows about the mode selector, not files/artifacts).
  const scopeSidebar = useSidebarConfig({
    pageType: 'scope',
    isLoggedIn: headerConfig.isLoggedIn,
    onResetConversation: () => {},
    onNavigateToLanding: () => navigate('/'),
    // Sub-pages don't drive mode switching — go back to the conversation
    // page (scope home) with the picked mode.
    currentMode: 'clarify',
    onModeChange: (mode) => {
      if (slug && scopeParam)
        navigate(`/${slug}/${scopeParam}/new${mode === 'clarify' ? '' : `?mode=${mode}`}`);
    },
    onCreateScopeConversation: () => {
      if (slug && scopeParam) navigate(`/${slug}/${scopeParam}/new`);
    },
    scopeConversations: scopeConversationList.map((c) => ({ id: c.id, title: c.title })),
    isLoadingScopeConversations,
    scopeActiveConversationId: null,
    onSelectScopeConversation: (id) => {
      if (slug && scopeParam) navigate(`/${slug}/${scopeParam}/${id}`);
    },
    onOpenScopeFiles:
      slug && scopeParam ? () => navigate(`/${slug}/${scopeParam}/files`) : undefined,
    onOpenScopeArtifacts:
      slug && scopeParam ? () => navigate(`/${slug}/${scopeParam}/artifacts`) : undefined,
  });

  const scopeSectionId: ScopeNavSection | undefined = isScopeMode ? props.scopeSection : undefined;
  // Map our shell prop → sidebar item id. Clarify is served by the mode
  // pill (`uncover`); Files / Artifacts map to their own ids.
  const activeScopeItemId = scopeSectionId === 'clarify' ? 'clarify' : (scopeSectionId ?? null);

  // `useSidebarConfig` sets `isActive` on mode items purely from
  // `currentMode`, so a sub-page (Files / Artifacts) whose mode we passed
  // as 'clarify' would otherwise highlight Clarify *too*. Force every
  // navigable item's active state from *our* target so exactly one item is
  // highlighted at a time.
  const scopeNavItems = isScopeMode
    ? scopeSidebar.navigationItems.map((item: any) =>
        item.type === 'header' ? item : { ...item, isActive: item.id === activeScopeItemId }
      )
    : [];

  const navItems = isScopeMode
    ? scopeNavItems
    : buildStartupNavItems({
        activeSection: props.activeSection!,
        onNavigateToHome: slug ? () => navigate(`/${slug}/home`) : undefined,
        onNavigateToWorkScopes: slug ? () => navigate(`/${slug}/work-scopes`) : undefined,
        onNavigateToArtifacts: slug ? () => navigate(`/${slug}/artifacts`) : undefined,
        onNavigateToFiles: slug ? () => navigate(`/${slug}/files`) : undefined,
        onNavigateToDesignSystem: slug ? () => navigate(`/${slug}/design-system`) : undefined,
        onNavigateToStartupProfile: slug ? () => navigate(`/${slug}/profile`) : undefined,
        onNavigateToIntegrations: slug ? () => navigate(`/${slug}/integrations`) : undefined,
      });

  // Back button — one flag drives whether it renders at all, so it can't show a
  // half-state (label without arrow):
  //   - Explicit `backTo(slug, scopeId)` wins (e.g. a startup detail → its list).
  //   - Scope pages (files/artifacts/clarify) → up to Work Scopes.
  //   - Level-0 startup pages (home, work-scopes, artifacts, …) have no "up",
  //     so there is no back button.
  const backDestination: string | null = backTo
    ? backTo(slug, isScopeMode ? scopeParam : undefined)
    : isScopeMode
      ? `/${slug}/work-scopes`
      : null;
  const showBack = !hideBackButton && backDestination !== null;

  // Only render 404 after the startups query has succeeded and is not
  // mid-refetch — `isFetched` alone stays true on a failed query and during
  // cache invalidation (when data is []), which would flash a false 404 for
  // a valid slug. Skip on error so an API failure is not shown as "not found".
  if (
    hasAttemptedLoad &&
    !isFetchingStartups &&
    !isStartupsError &&
    (!matchedStartup || !startupId)
  ) {
    return (
      <NotFoundState
        description="Startup not found"
        actionLabel="Back to My Startups"
        onAction={() => navigate('/startups')}
      />
    );
  }

  return (
    <AppLayout
      sidebar={{ navigationItems: navItems, onNavigateToLanding: () => navigate('/') }}
      user={{ isLoggedin: headerConfig.isLoggedIn, name: headerConfig.user?.name }}
      userMenuProps={{
        points: headerConfig.points,
        pointsLabel: 'Points',
        menuItems: headerConfig.userMenuItems,
        socialLinks: headerConfig.userMenuSocialLinks,
        onRequestUpgrade: headerConfig.onRequestUpgrade,
        isRequestingUpgrade: headerConfig.isRequestingUpgrade,
        canRequestUpgrade: true,
        isWeb: true,
      }}
      isWeb
      height="screen"
      defaultSidebarCollapsed={false}
    >
      <ShellHeader
        headerProps={headerConfig.headerProps}
        startupName={props.headerTitle ?? scopeHeaderTitle}
        startupId={startupId ?? ''}
        showBack={showBack}
        onBack={() => backDestination && navigate(backDestination)}
        headerActions={props.headerActions}
        // "New Scope" is a startup-level action — no reason to offer it while
        // the user is already scoped inside one. A page can override (e.g. Work
        // Scopes renders its own button wired to the list's facade).
        showNewScopeAction={props.showNewScopeAction ?? !isScopeMode}
        creditLimitBanner={
          creditLimitBanner.isVisible
            ? {
                ...creditLimitBanner,
                onAction: () => navigation.toBilling(),
                onDismiss: () => creditLimitBanner.setVisible(false),
              }
            : undefined
        }
      />

      {matchedStartup && startupId ? children({ startup: matchedStartup, startupId }) : null}
    </AppLayout>
  );
}

/**
 * Renders the startup header. The "New Scope" action is delegated to the scope
 * module's {@link NewScopeAction} so all scope-create wiring lives in the
 * module, not this shared shell.
 */
function ShellHeader({
  headerProps,
  startupName,
  startupId,
  showBack,
  onBack,
  creditLimitBanner,
  showNewScopeAction,
  headerActions,
}: {
  headerProps: Record<string, any>;
  startupName: string;
  startupId: string;
  showBack: boolean;
  onBack: () => void;
  creditLimitBanner: any;
  showNewScopeAction: boolean;
  headerActions?: ReactNode;
}): JSX.Element {
  return (
    <Header
      {...headerProps}
      // Always show the header title (startup / scope name); only show the back
      // arrow when there's an "up" destination.
      showBackButton
      showBackArrow={showBack}
      backText={startupName}
      onBack={onBack}
      isHasBorder
      className="sticky top-0 w-full"
      isWeb
      creditLimitBanner={creditLimitBanner}
      rightActions={
        <div className="flex items-center gap-2">
          {headerActions}
          {showNewScopeAction && <NewScopeAction startupId={startupId} />}
        </div>
      }
    />
  );
}

export default StartupSubPageShell;
